import { useMemo } from "react";

interface CyclingTSSCalendarProps {
  data: Record<string, unknown>;
}

const INTENSITY_COLORS = [
  "bg-emerald-900/30",
  "bg-emerald-700/40",
  "bg-emerald-500/50",
  "bg-amber-500/50",
  "bg-red-500/50",
];

function getIntensityClass(tss: number): string {
  if (tss < 50) return INTENSITY_COLORS[0];
  if (tss < 100) return INTENSITY_COLORS[1];
  if (tss < 150) return INTENSITY_COLORS[2];
  if (tss < 250) return INTENSITY_COLORS[3];
  return INTENSITY_COLORS[4];
}

export function CyclingTSSCalendar({ data }: CyclingTSSCalendarProps) {
  const powerCurve = data.power_curve as Array<{ duration_label?: string; watts?: number }> | undefined;

  const curveData = useMemo(() => {
    if (!powerCurve?.length) return [];
    return powerCurve.map((p) => ({
      label: p.duration_label ?? "",
      watts: p.watts ?? 0,
    }));
  }, [powerCurve]);

  const plateauDetected = data.ftp_plateau as { is_plateau?: boolean; message?: string } | undefined;

  return (
    <div className="rounded-xl border border-border/60 p-4">
      <h3 className="text-sm font-semibold mb-3">Power Profile</h3>

      {curveData.length > 0 ? (
        <div className="space-y-3">
          {/* Power curve bars */}
          <div className="space-y-1.5">
            {curveData.map((point) => {
              const maxWatts = Math.max(...curveData.map((p) => p.watts), 1);
              const pct = (point.watts / maxWatts) * 100;
              return (
                <div key={point.label} className="flex items-center gap-2">
                  <div className="w-12 text-[10px] text-muted-foreground text-right font-mono">{point.label}</div>
                  <div className="flex-1 h-5 rounded bg-white/[0.04] overflow-hidden">
                    <div
                      className={`h-full rounded ${getIntensityClass(point.watts)}`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <div className="w-14 text-xs font-mono tabular-nums text-foreground text-right">{point.watts}W</div>
                </div>
              );
            })}
          </div>

          {/* Plateau warning */}
          {plateauDetected?.is_plateau && (
            <div className="text-[10px] font-mono text-amber-400/80 bg-amber-500/10 rounded px-2 py-1.5 border border-amber-500/20">
              ⚠ {plateauDetected.message ?? "FTP plateau detected — consider structured intervals"}
            </div>
          )}
        </div>
      ) : (
        <div className="h-[200px] flex items-center justify-center text-muted-foreground text-sm">
          No power profile data yet. Record cycling activities with a power meter.
        </div>
      )}
    </div>
  );
}
